import React, { FormEvent, useMemo } from 'react';
import { UseBoolean } from 'react-hanger';
import { GridColumnMenuItemContent, GridColumnMenuItemGroup } from '@progress/kendo-react-grid';
import cn from 'clsx';
import Button from '@components-clean/core/button';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import GridMenuButton, { GridMenuButtonItem } from '@components-clean/core/grid/grid-menu/grid-menu-button';
import style from '@components-clean/core/grid/grid-menu/grid-settings-menu/style.module.scss';
import {
    GridColumnProps,
    REORDER_COLUMN_ID,
    ROW_MENU_ACTION_COLUMN_ID,
} from '@components-clean/core/grid/grid.interface';
import { useTranslation } from '@hooks/index';
import { labelToTestId } from '@utils/convertor';

interface GridSettingsMenuItemProps {
    columns: GridColumnProps[];
    onSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onReset: (event: FormEvent<HTMLFormElement>) => void;
    onToggle: (idx: number) => void;
    onToggleChild: (idx: number, childIdx: number) => void;
    fieldName: keyof Pick<GridColumnProps, 'locked' | 'show'>;
    buttonProps: Pick<GridMenuButtonItem, 'icon' | 'label'>;
    menuExpand: UseBoolean;
    type: 'lock' | 'show';
    atLeastOneSelectionRequired?: boolean;
}

const GridSettingsMenuItem = ({
    columns,
    onSubmit,
    onReset,
    onToggle,
    onToggleChild,
    fieldName,
    buttonProps,
    menuExpand,
    type,
    atLeastOneSelectionRequired,
}: GridSettingsMenuItemProps) => {
    const { t } = useTranslation();
    const initialState = useMemo(() => columns, []);
    const isDirty = JSON.stringify(initialState) !== JSON.stringify(columns);

    const isOnlyOneChecked = useMemo(
        () => columns.reduce((count, column) => count + (column[fieldName] ? 1 : 0), 0) === 1,
        [columns, fieldName],
    );

    const isDisabled = (column: GridColumnProps, isChecked: boolean) => {
        const overrideDisable = atLeastOneSelectionRequired && isOnlyOneChecked && isChecked;

        switch (type) {
            case 'lock':
                return overrideDisable || column.disableLock;
            case 'show':
            default:
                return overrideDisable;
        }
    };

    return (
        <GridColumnMenuItemGroup className="!k-border-0">
            <GridMenuButton
                item={{
                    ...buttonProps,
                    isActive: menuExpand.value,
                }}
                onClick={menuExpand.toggle}
                rightArrow
            />
            <GridColumnMenuItemContent show={menuExpand.value}>
                <div className={cn('k-column-list-wrapper', style.container)}>
                    <form onSubmit={onSubmit} onReset={onReset}>
                        <div className="k-column-list">
                            {columns.flatMap((column, idx) => {
                                if (
                                    column.id === REORDER_COLUMN_ID ||
                                    column.id === ROW_MENU_ACTION_COLUMN_ID ||
                                    column.columnType === 'checkbox'
                                )
                                    return [];

                                if (column.children && column.enhancedGroupedColumnToggling && type !== 'lock') {
                                    return [
                                        <div key={column.id} className={cn('k-px-2 k-py-1', style.checkboxList)}>
                                            <b>{column.title}</b>
                                        </div>,
                                        ...column.children.flatMap((child, childidx) => {
                                            return [
                                                <div key={child.id} className={cn('k-px-2 k-py-1', style.checkboxList)}>
                                                    <CoreCheckbox
                                                        labelClassName={style.label}
                                                        label={child.title}
                                                        data-testid={`checkbox-${String(idx)}-${labelToTestId(child.title ?? String(childidx))}`}
                                                        onClick={() => {
                                                            onToggleChild(idx, childidx);
                                                        }}
                                                        checked={!!child[fieldName]}
                                                        disabled={isDisabled(child, !!child[fieldName])}
                                                    />
                                                </div>,
                                            ];
                                        }),
                                    ];
                                }

                                return [
                                    <div key={column.id} className={cn('k-px-2 k-py-1', style.checkboxList)}>
                                        <CoreCheckbox
                                            labelClassName={style.label}
                                            label={column.title}
                                            data-testid={`checkbox-${labelToTestId(column.title ?? String(idx))}`}
                                            onClick={() => {
                                                onToggle(idx);
                                            }}
                                            checked={!!column[fieldName]}
                                            disabled={isDisabled(column, !!column[fieldName])}
                                        />
                                    </div>,
                                ];
                            })}
                        </div>
                        <div className="k-actions k-hstack k-justify-content-stretch">
                            <Button
                                type="reset"
                                data-testid="button-reset"
                                alternativeStyle
                                themeColor="secondary"
                                fillMode="flat"
                            >
                                {t('reset')}
                            </Button>
                            <Button
                                type="submit"
                                data-testid="button-apply"
                                alternativeStyle
                                primary
                                disabled={!isDirty}
                            >
                                {t('save')}
                            </Button>
                        </div>
                    </form>
                </div>
            </GridColumnMenuItemContent>
        </GridColumnMenuItemGroup>
    );
};

export default GridSettingsMenuItem;
